import { useMemo } from "react";

import { Switch, Textarea } from "@aircall/ds";
import { Loader2, Sparkle } from "@aircall/react-icons";
import { Maybe } from "graphql/jsutils/Maybe";
import { useTranslation } from "react-i18next";

import { pickIcon } from "./EvaluationField.utilities";

import {
  InputChoice,
  InputChoiceIcon,
  InputChoiceLabel,
  InputChoices,
} from "@/blocks/InputChoices";
import { Slider, SliderProps } from "@/components/Slider";
import { BinaryAnswerValue } from "@/graphql/generated/graphql";

import { EvaluationFieldProps } from ".";
import { ExcludeNotApplicable } from "../EvaluationResult/EvaluationResult.decl";

const noop = () => {};

export function EvaluationTextarea<Value extends string>({
  comment,
  loading,
  aiAnswersTakingLonger,
  hasAiAnswer,
  onChangeComment,
}: Pick<
  EvaluationFieldProps<Value>,
  | "comment"
  | "loading"
  | "aiAnswersTakingLonger"
  | "hasAiAnswer"
  | "onChangeComment"
>) {
  const { t } = useTranslation();

  return (
    <div className="flex flex-col gap-2">
      <Textarea
        value={comment}
        onChange={onChangeComment}
        placeholder={loading ? undefined : t("evaluation.add_feedback")}
        disabled={loading}
        style={{
          cursor: loading ? "not-allowed" : "auto",
        }}
      />
      {loading && (
        <div className="flex items-center gap-1">
          <Loader2 className="size-4 animate-spin" />
          <p className="text-xs font-normal">
            {aiAnswersTakingLonger
              ? t("evaluation.checking_ai_answers_taking_longer")
              : t("evaluation.checking_ai_answers")}
          </p>
        </div>
      )}
      {!loading && hasAiAnswer && (
        <div className="flex items-center gap-1">
          <span
            className="text-xs font-medium text-muted-foreground"
            data-test="generated-by-ai"
          >
            {t("evaluation.has_ai_answer")}
          </span>
          <Sparkle className="size-4" />
        </div>
      )}
    </div>
  );
}

function InputSlider<Value extends string>({
  onChange,
  value,
  ratingScale,
  disabled,
}: Pick<EvaluationFieldProps<Value>, "ratingScale"> & {
  onChange: EvaluationFieldProps<Value>["onChangeRating"];
  value: Maybe<Value>;
  disabled?: boolean;
}) {
  const { t } = useTranslation();

  const ratingScaleWithoutNotApplicable = useMemo(() => {
    const { NOT_APPLICABLE: _, ...rest } = ratingScale as {
      NOT_APPLICABLE: number;
    } & Record<Value, number>;

    return rest;
  }, [ratingScale]);

  const entries = useMemo(
    () => Object.entries<number>(ratingScaleWithoutNotApplicable),
    [ratingScaleWithoutNotApplicable]
  );

  /**
   * The rating scale is ordinal, so position the marks evenly by their index
   * rather than by their point value. Driving the slider in the points domain
   * bunches the marks together (and collides the labels) whenever a scorecard
   * weights its answers unevenly, and makes the drag stops land on values that
   * don't match any answer.
   */
  const marks: SliderProps["marks"] = useMemo(
    () =>
      entries.map(([key], index) => ({
        value: index,
        label: t(key.toLowerCase()),
      })),
    [entries, t]
  );

  const selectedIndex = useMemo(() => {
    if (!value) {
      return 0;
    }

    // `value` can be NOT_APPLICABLE, which is absent from `entries`; findIndex
    // then returns -1 and the slider would render an out-of-range thumb/fill.
    const index = entries.findIndex(([key]) => key === value);

    return index < 0 ? 0 : index;
  }, [entries, value]);

  const onChangeSlider = (index: number) => {
    const found = entries[index];

    if (!found) {
      return null;
    }

    const rating = found.at(0) as Value;

    onChange?.(rating);
  };

  return (
    <div className="mx-6">
      <Slider
        onMouseDown={() => {
          if (value === null) {
            onChangeSlider(selectedIndex);
          }
        }}
        value={selectedIndex}
        min={0}
        max={entries.length - 1}
        onChange={onChangeSlider}
        step={1}
        marks={marks}
        disabled={disabled}
      />
    </div>
  );
}

function TotalPoints<Value extends string>({
  ratingScale,
  maximumPoints,
  notApplicable,
  rating,
}: Pick<
  EvaluationFieldProps<Value>,
  "maximumPoints" | "notApplicable" | "ratingScale" | "rating"
>) {
  const { t } = useTranslation();

  if (notApplicable) {
    return (
      <p className="text-xs font-normal text-muted-foreground">
        {t("evaluation.question_summary_points", {
          text: "--",
        })}
      </p>
    );
  }

  if (!rating) {
    return (
      <p className="text-xs font-normal text-muted-foreground">
        {t("evaluation.question_summary_points", {
          text: `0 / ${maximumPoints}`,
        })}
      </p>
    );
  }

  return (
    <p className="text-xs font-normal text-muted-foreground">
      {t("evaluation.question_summary_points", {
        text: `${ratingScale[rating]} / ${maximumPoints}`,
      })}
    </p>
  );
}

export function PickInputChoices<Value extends string>({
  mode,
  value,
  disabled,
  ratingScale,
  onChange,
}: Pick<InputChoices.Props<Value>, "mode" | "value" | "onChange"> &
  Pick<InputChoice.Props<Value>, "disabled"> &
  Pick<EvaluationFieldProps<Value>, "ratingScale">) {
  const { t } = useTranslation();

  if (mode === "binary") {
    return (
      <InputChoices mode={mode} value={value} onChange={onChange}>
        {Object.keys(ratingScale)
          .filter((value) => value !== "NOT_APPLICABLE")
          .map((value) => {
            const Icon = pickIcon(
              value as ExcludeNotApplicable<BinaryAnswerValue>
            );

            return (
              <InputChoice key={value} disabled={disabled} value={value}>
                {Icon !== null && (
                  <InputChoiceIcon>
                    <Icon className="size-4" />
                  </InputChoiceIcon>
                )}
                <InputChoiceLabel>{t(value.toLowerCase())}</InputChoiceLabel>
              </InputChoice>
            );
          })}
      </InputChoices>
    );
  }

  if (mode === "multiple") {
    return (
      <InputSlider
        onChange={onChange}
        value={value}
        ratingScale={ratingScale}
        disabled={disabled}
      />
    );
  }

  throw new Error("Invalid mode");
}
export function EvaluationField<Value extends string>({
  mode,
  rank,
  question,
  ratingScale,
  maximumPoints,
  rating,
  comment,
  description,
  notApplicable,
  possiblyNotApplicable,
  renderInputChoices,
  renderNotApplicableToggle,
  renderFeedback,
  loading = false,
  aiAnswersTakingLonger = false,
  hasAiAnswer = false,
  onChangeRating = noop,
  onChangeComment = noop,
  onChangeNotApplicable = noop,
}: EvaluationFieldProps<Value>) {
  const { t } = useTranslation();

  const overflowedRating = useMemo(
    () =>
      Object.values<number>(ratingScale).find(
        (rating) => rating > maximumPoints
      ),
    [maximumPoints, ratingScale]
  );

  if (overflowedRating) {
    throw new Error(
      `Rating scale overflowed. Maximum points is ${maximumPoints} but rating scale has a value of ${overflowedRating}`
    );
  }

  return (
    <div className="flex flex-1 gap-2">
      <p className="text-right text-base font-semibold">{`${rank}.`}</p>
      <div className="flex flex-1 flex-col gap-2">
        <div className="flex justify-between gap-4">
          <p className="text-base font-semibold">{question}</p>
          {Boolean(possiblyNotApplicable) && (
            <div className="flex items-center gap-1">
              <span className="text-sm font-normal">{t("not_applicable")}</span>
              {renderNotApplicableToggle ?? (
                <Switch
                  checked={notApplicable}
                  onCheckedChange={onChangeNotApplicable}
                  disabled={loading}
                />
              )}
            </div>
          )}
        </div>
        {renderInputChoices ?? (
          <PickInputChoices
            mode={mode}
            value={rating}
            onChange={onChangeRating}
            ratingScale={ratingScale}
            disabled={loading || notApplicable}
          />
        )}
        <div className="flex justify-between">
          <div className="flex flex-col gap-1">
            {Boolean(notApplicable) && (
              <p className="text-sm font-medium">{t("not_applicable_full")}</p>
            )}
            {Boolean(description) && (
              <p className="text-xs font-normal">{description}</p>
            )}
          </div>
          <TotalPoints
            rating={rating}
            ratingScale={ratingScale}
            maximumPoints={maximumPoints}
            notApplicable={notApplicable}
          />
        </div>
        {renderFeedback ?? (
          <EvaluationTextarea
            comment={comment}
            loading={loading}
            aiAnswersTakingLonger={aiAnswersTakingLonger}
            hasAiAnswer={hasAiAnswer}
            onChangeComment={onChangeComment}
          />
        )}
      </div>
    </div>
  );
}
